// La tabla de mejores parejas: las que más ganan jugando juntas.
//
// Por resultados y no por Elo, por la misma razón que el campeón del mes va por
// puntos: "vs. esperado" premia a la pareja que sorprende, y la pareja de los
// dos mejores del grupo nunca encabezaría nada. Eso ya lo cuenta el perfil de
// cada uno ("con quién rinde"); acá la pregunta es otra: ¿quiénes ganan más?
//
// Para que dos partidos de suerte no encabecen la tabla, el porcentaje se
// suaviza (como si cada pareja arrancara con un ganado y un perdido) y hace
// falta un mínimo de partidos juntos para entrar.

import { MINIMO_PARA_OPINAR } from './perfil';
import type { IdJugador, PartidoParaElo } from './tipos';

export const MINIMO_PAREJA = MINIMO_PARA_OPINAR;

export interface FilaPareja {
  jugadores: [IdJugador, IdJugador];
  partidos: number;
  ganados: number;
  perdidos: number;
  games_favor: number;
  games_contra: number;
  /** Lo que ordena: (ganados + 1) / (partidos + 2). */
  indice: number;
}

export function armarParejas(partidos: readonly PartidoParaElo[]): FilaPareja[] {
  const porPareja = new Map<string, FilaPareja>();

  const sumar = (pareja: readonly [IdJugador, IdJugador], gano: boolean, favor: number, contra: number) => {
    const [a, b] = pareja[0] < pareja[1] ? [pareja[0], pareja[1]] : [pareja[1], pareja[0]];
    const llave = `${a}-${b}`;
    const fila = porPareja.get(llave) ?? {
      jugadores: [a, b] as [IdJugador, IdJugador],
      partidos: 0,
      ganados: 0,
      perdidos: 0,
      games_favor: 0,
      games_contra: 0,
      indice: 0,
    };
    fila.partidos += 1;
    if (gano) fila.ganados += 1;
    else fila.perdidos += 1;
    fila.games_favor += favor;
    fila.games_contra += contra;
    porPareja.set(llave, fila);
  };

  for (const p of partidos) {
    const ganoA = p.games_a > p.games_b;
    sumar(p.equipoA, ganoA, p.games_a, p.games_b);
    sumar(p.equipoB, !ganoA, p.games_b, p.games_a);
  }

  return [...porPareja.values()]
    .filter((f) => f.partidos >= MINIMO_PAREJA)
    .map((f) => ({ ...f, indice: (f.ganados + 1) / (f.partidos + 2) }))
    .sort(
      (x, y) =>
        y.indice - x.indice ||
        y.partidos - x.partidos ||
        y.games_favor - y.games_contra - (x.games_favor - x.games_contra) ||
        x.jugadores[0] - y.jugadores[0] ||
        x.jugadores[1] - y.jugadores[1],
    );
}
